<script>
import Vue from "vue";
import multiselect from "vue-multiselect";

export default Vue.extend({
	inheritAttrs: false,
	components: {
		"multiselect": multiselect,
	},
	props: {
		label: {
			type: String,
			default: "",
		},
		name: {
			type: String,
			default: "",
		},
		value: {
			type: [Array, Object, String, null],
			default: "",
		},
		theme: String,
		optionLabel: String
	},
	data() {
		return {
			id: null,
		};
	},
	computed: {
		optsAreObjects() {
			if (!this.$attrs.options) {
				return false;
			}
			const sampleOpt = this.$attrs.options[0];
			return !(
				(typeof sampleOpt === "string" || sampleOpt instanceof String)
				|| (typeof sampleOpt === "number" && isFinite(sampleOpt))
			);
		},
		multiselectLabel() {
			if (!this.optsAreObjects) {
				return "";
			}
			return this.optionLabel || "label";
		},
		multiselectTrackBy() {
			if (!this.optsAreObjects) {
				return "";
			}
			return this.$attrs.trackBy || "value";
		},
		listeners() {
			return {
				...this.$listeners,
				input: value => this.$emit("input", value),
			};
		},
	},
	mounted() {
		this.id = "multiselect-" + "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
			var r = Math.random() * 16 | 0, v = c == "x" ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}
});
</script>

<template>
	<span class="select">
		<label v-if="label" :for="id">{{ label }}</label>
		<multiselect
			:id="id"
			:name="name"
			:value="value"
			:label="multiselectLabel"
			:track-by="multiselectTrackBy"
			v-bind="$attrs"
			v-on="listeners"
			:class="theme"
		></multiselect>
		<div class="help-text text-small">
			<slot name="error"></slot>
		</div>
	</span>
</template>

<style lang="less">
@import (reference) "../assets/variables.less";
// @import "~vue-multiselect/dist/vue-multiselect.min.css"; // Turn this on to only see what it does to override

.multiselect,
.multiselect__input {
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	position: relative;
	border-radius: @border-radius;
	box-sizing: border-box;
	border: 1px solid @ui-light-6;
	outline: none;
	width: 100%;
}
.multiselect {
	font-family: @font-family-IntCircular-Light;
	font-size: @font-size-14;
	font-weight: @weight-light;
	line-height: @font-size-18;
	padding: @gap-smallest @gap-small;
}
.multiselect__input {
	max-width: 100%;
	line-height: @font-size-16;
	padding: 3px 8px 3px;
	margin: -4px 0 -5px -8px;
	z-index: 10;
	&::placeholder {
		font-style: italic;
	}
	.multiselect--active & {
		min-width: calc(100% - @gap-smallest);
	}
	.multiselect:not(.multiselect--active) .multiselect__tags &,
	.multiselect:not(.multiselect--active) .multiselect__tags &:hover {
		border-color: transparent;
		background-color: transparent;
	}
}

.multiselect__select {
	cursor: pointer;
	position: absolute;
	width: 26px;
	height: 25px;
	right: 1px;
	top: 1px;
	padding: 4px 4px;
	text-align: center;
	-webkit-transition: -webkit-transform .2s ease;
	transition: -webkit-transform .2s ease;
	transition: transform .2s ease;
	transition: transform .2s ease, -webkit-transform .2s ease;
	&:before {
		position: relative;
		right: 0;
		top: 65%;
		color: #999;
		margin-top: 4px;
		border-style: solid;
		border-width: 5px 5px 0;
		border-color: #999 transparent transparent;
		content: "";
	}
	.multiselect--active & {
		transform: rotate(180deg);
	}
}
.multiselect__tags-wrap {
	margin-left: -@gap-smallest;
}
.multiselect__tag {
	border-radius: @border-radius;
	border-radius: @border-radius;
	padding: 1px @gap-smallest;
	margin-right: @gap-smallest;
}

.multiselect__content-wrapper {
	z-index: 10;
	position: absolute;
	padding: @gap-smallest 0;
	overflow-y: auto;
	margin-top: 7px;
	margin-left: -@gap-small;
}
.multiselect__option {
	display: block;
	padding: @gap-smallest @gap-small;
}


.light-theme,
.dark-theme .light-theme {
	.multiselect,
	&.multiselect {
		border: 1px solid @ui-light-6;
		&:hover, .hover &,
		&:hover .multiselect__input, .hover & .multiselect__input {
			color: @ui-dark-1;
			background-color: @ui-light-3;
		}
		&:hover, .hover & {
			border-color: @ui-light-7;
		}
	}
	.multiselect__input {
		color: @ui-dark-7;
		background-color: @ui-light-1;
		border-color: @primary-dark-4;
		&::placeholder {
			color: @ui-dark-8;
		}
	}

	.multiselect__placeholder {
		color: @ui-light-8;
	}
	.multiselect__tag {
		background-color: @ui-light-4;
	}

	.multiselect__content-wrapper {
		background-color: @ui-light-2;
		border: 1px solid @ui-light-6;
	}
	.multiselect__element {
		color: @ui-dark-7;
	}
	.multiselect__option--selected {
		color: @ui-dark-4;
		background-color: @ui-light-5;
	}
	.multiselect__option--highlight {
		color: @ui-light-2;
		background-color: @primary-dark-4;
		text-decoration: none;
	}
}

.dark-theme {
	.multiselect,
	&.multiselect {
		border: 1px solid @ui-dark-6;
		&:hover, .hover &,
		&:hover .multiselect__input, .hover & .multiselect__input {
			color: @ui-light-1;
			background-color: @ui-dark-3;
		}
		&:hover, .hover & {
			border-color: @ui-dark-7;
		}
	}
	.multiselect__input {
		color: @ui-light-7;
		background-color: @ui-dark-1;
		border-color: @primary-light-4;
		&::placeholder {
			color: @ui-light-8;
		}
	}

	.multiselect__placeholder {
		color: @ui-light-8;
	}
	.multiselect__tag {
		background-color: @ui-dark-4;
	}

	.multiselect__content-wrapper {
		background-color: @ui-dark-2;
		border: 1px solid @ui-dark-6;
	}
	.multiselect__element {
		color: @ui-light-7;
	}
	.multiselect__option--selected {
		color: @ui-light-4;
		background-color: @ui-dark-5;
	}
	.multiselect__option--highlight {
		color: @ui-dark-2;
		background-color: @primary-light-4;
		text-decoration: none;
	}
}
</style>
